import {
  UDevGlobals,
  getD3DevTopCenter,
  getDeviceById,
  getGroupsOfDevice,
  getNote,
  getRoomById,
  getSerialById,
  isPointSnapped,
  rebuildWallsCache
} from "src/components/Planner/planner/uDev";
import { removeDevices, setAndSendSerialPosition } from "src/components/Planner/planner/uDev-dom";
import { getSerialConnectedPair, getSerialConnectionForSerialId, getSerialConnectionProps } from "src/components/Planner/planner/serialConnections";
import {
  addNewDesignIdToDefaultSerial,
  ajaxSetSerialProps,
  connectSerials,
  detachSerialConnection,
  getSerialD
} from "src/components/Planner/planner/serialConnections-dom";
import { DisconnectSerialsAction } from 'src/components/Planner/planner/history/serconn-actions';
import { DeleteSingleDeviceAction } from 'src/components/Planner/planner/history/device-actions';
import { callIfFn } from 'src/components/Planner/planner/history';
import { PlannerAction } from 'src/components/Planner/planner/history/planner-action';
import { ts } from 'src/utils/translate';
import {
    isDeviceInIntendedRoom,
    isPlannerMetric,
    snappedPtToPosData,
} from 'src/components/Planner/planner/planner';
import { PlannerGlobals } from 'src/components/Planner/planner/planner-globals';
import { getDColsNum, getDRowsNum } from 'src/components/Planner/planner/serial-layout';
import { inchToCm } from 'src/utils/imperialConverter';
import { redrawDevices } from 'src/components/Planner/planner/deviceDrawing';
import { snapDeviceToWall, uMath } from 'src/components/Planner/planner/uMath';

// Default-frame placement heights: when the user opens an "empty new frame" in
// adjust frame, we pick a sensible default height for it. If the existing serial
// sits low (e.g. socket strip at 30 cm) we default the new frame to a high
// position (140 cm, switch-typical), and vice versa.
const FRAME_HEIGHT_SPLIT_THRESHOLD_CM = 139;
const DEFAULT_LOW_FRAME_HEIGHT_CM = 30;
const DEFAULT_HIGH_FRAME_HEIGHT_CM = 140;
const DEFAULT_LOW_FRAME_HEIGHT_INCH = 12;
const DEFAULT_HIGH_FRAME_HEIGHT_INCH = 55;

// Wall-snap search radius when re-snapping a frame after split/merge. Empirically
// "10 device widths" was enough in the legacy planner to find a nearby wall slot
// without picking too-distant snaps.
const SNAP_SEARCH_RADIUS_DEVICE_WIDTHS = 10;

export type FrameDirection = 'horizontal' | 'vertical';

export interface FrameState {
    id: string;
    deviceIds: string[];
    direction: FrameDirection;
    height: number;
    roomId: number;
    isNew?: boolean;
}

export interface AdjustFrameState {
    serial: any;
    devices: Record<string, any>;
    frames: Record<string, FrameState>;
    visible: boolean;
    loading: false | 'detach' | 'confirm';
    dragging: boolean;
    posProps?: { x: number; y: number; angle: number; roomId: number };
}

export type AdjustFrameAction =
    | { type: 'ON_DRAG_START' }
    | { type: 'ON_DRAG_END'; payload: any }
    | { type: 'SET_LOADING'; payload: false | 'detach' | 'confirm' }
    | { type: 'SET_HEIGHT'; payload: { frameId: string; cm: number } }
    | { type: 'TOGGLE_DIRECTION'; payload: { frameId: string } };

export function buildInitialState(serialId: number | string): AdjustFrameState | null {
    const serial = getSerialById(serialId);
    if (!serial) return null;

    const conn = getSerialConnectionForSerialId(serial.id);
    if (conn) {
        const props: any = getSerialConnectionProps(conn.id);
        if (!props) return null;
        const devices: Record<string, any> = {};
        const frames: Record<string, FrameState> = {};

        props.serials.forEach((ser: any) => {
            const serAllDevIds: string[] = [];
            ser.serObj.devices.forEach((dev: any) => {
                if (Array.isArray(dev.similarDeviceIds)) {
                    dev.similarDeviceIds.forEach((simDevId: any) => {
                        const simDev = getDeviceById(simDevId);
                        if (simDev) {
                            const id = simDev.deviceId + '';
                            devices[id] = Object.assign({}, simDev);
                            devices[id].deviceId = id;
                            serAllDevIds.push(id);
                        }
                    });
                } else {
                    const id = dev.deviceId + '';
                    devices[id] = Object.assign({}, dev);
                    devices[id].deviceId = id;
                    serAllDevIds.push(id);
                }
            });
            frames['frame-' + ser.serialId] = {
                id: 'frame-' + ser.serialId,
                deviceIds: serAllDevIds,
                direction: ser.direction,
                height: ser.height,
                roomId: ser.roomId,
            };
        });

        return { serial, devices, frames, visible: true, loading: false, dragging: false };
    }

    const deviceIds: string[] = serial.deviceIds.map((id: any) => id + '');
    const devices: Record<string, any> = {};
    deviceIds.forEach((id) => {
        const dev = getDeviceById(id);
        if (dev) {
            devices[id] = Object.assign({}, dev);
            devices[id].deviceId = id;
        }
    });

    const baseFrame: FrameState = {
        id: 'frame-' + serial.id,
        deviceIds,
        direction: serial.direction,
        height: serial.height,
        roomId: serial.roomId,
    };

    const metric = isPlannerMetric();
    const lowDefault = metric ? DEFAULT_LOW_FRAME_HEIGHT_CM : inchToCm(DEFAULT_LOW_FRAME_HEIGHT_INCH);
    const highDefault = metric ? DEFAULT_HIGH_FRAME_HEIGHT_CM : inchToCm(DEFAULT_HIGH_FRAME_HEIGHT_INCH);

    const newFrame: FrameState = {
        isNew: true,
        id: 'frame-new',
        deviceIds: [],
        direction: 'horizontal',
        height: serial.height > FRAME_HEIGHT_SPLIT_THRESHOLD_CM ? lowDefault : highDefault,
        roomId: serial.roomId,
    };

    return {
        serial,
        devices,
        frames: { [baseFrame.id]: baseFrame, [newFrame.id]: newFrame },
        visible: true,
        loading: false,
        dragging: false,
    };
}

function newStateFromOnDragEnd(state: AdjustFrameState, result: any): AdjustFrameState {
    const { destination, source, draggableId } = result;
    if (!destination) return { ...state, dragging: false };
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
        return { ...state, dragging: false };
    }

    const srcFrame = state.frames[source.droppableId];
    const newSrcFrameIds = Array.from(srcFrame.deviceIds);
    newSrcFrameIds.splice(source.index, 1);
    const newSrcFrame: FrameState = { ...srcFrame, deviceIds: newSrcFrameIds };

    const destFrame =
        source.droppableId === destination.droppableId
            ? newSrcFrame
            : state.frames[destination.droppableId];
    const newDestFrameIds = Array.from(destFrame.deviceIds);
    newDestFrameIds.splice(destination.index, 0, draggableId);
    const newDestFrame: FrameState = { ...destFrame, deviceIds: newDestFrameIds };

    if (newDestFrame.isNew && newSrcFrame.deviceIds.length === 0) {
        const swappedSrc: FrameState = {
            ...newSrcFrame,
            deviceIds: newDestFrame.deviceIds,
            height: newDestFrame.height,
        };
        const swappedDest: FrameState = {
            ...newDestFrame,
            deviceIds: [],
            height: newSrcFrame.height,
        };
        return {
            ...state,
            dragging: false,
            frames: {
                ...state.frames,
                [swappedSrc.id]: swappedSrc,
                [swappedDest.id]: swappedDest,
            },
        };
    }

    return {
        ...state,
        dragging: false,
        frames: {
            ...state.frames,
            [newSrcFrame.id]: newSrcFrame,
            [newDestFrame.id]: newDestFrame,
        },
    };
}

export function reducer(state: AdjustFrameState, action: AdjustFrameAction): AdjustFrameState {
    switch (action.type) {
        case 'ON_DRAG_START':
            return { ...state, dragging: true };
        case 'ON_DRAG_END':
            return newStateFromOnDragEnd(state, action.payload);
        case 'SET_LOADING':
            return { ...state, loading: action.payload };
        case 'SET_HEIGHT': {
            const { frameId, cm } = action.payload;
            if (!(frameId in state.frames) || cm < 0) return state;
            const frame = { ...state.frames[frameId], height: cm };
            return { ...state, frames: { ...state.frames, [frameId]: frame } };
        }
        case 'TOGGLE_DIRECTION': {
            const { frameId } = action.payload;
            if (!(frameId in state.frames)) return state;
            const frame = {
                ...state.frames[frameId],
                direction:
                    state.frames[frameId].direction === 'horizontal'
                        ? ('vertical' as FrameDirection)
                        : ('horizontal' as FrameDirection),
            };
            return { ...state, frames: { ...state.frames, [frameId]: frame } };
        }
        default:
            return state;
    }
}

function makeDeleteDeviceAction(dev: any, serial: any, wasLastOfSerial: boolean) {
    return new DeleteSingleDeviceAction(
        {
            device: dev,
            groups: getGroupsOfDevice(dev.deviceId),
            note: dev.noteId ? getNote(dev.deviceId, 'dev') : null,
            removedMeasures: [],
            serial,
            wasLastOfSerial,
            serialConnection: undefined,
        },
        true,
    );
}

interface UndoChainContext {
    action: any;
    oldDevId: any;
    oldSerId: any;
    newDevId: any;
}

function runUndoChain<T>(
    items: T[],
    getAction: (item: T) => any,
    onSuccess: (item: T, ctx: UndoChainContext) => void,
): Promise<void> {
    return items.reduce(
        (p: Promise<unknown>, item: T) =>
            p.finally(
                () =>
                    new Promise<void>((innerRes, innerRej) => {
                        const action = getAction(item);
                        const oldDevId = action.device.deviceId;
                        const oldSerId = action.serialId;
                        action.undo((result: boolean) => {
                            if (!result) return innerRej();
                            const newDevId = action.device.deviceId;
                            onSuccess(item, { action, oldDevId, oldSerId, newDevId });
                            innerRes();
                        });
                    }),
            ),
        Promise.resolve(),
    ) as Promise<void>;
}

function changeDevIdInState(state: AdjustFrameState, from: any, to: any) {
    for (const key in state.devices) {
        if (key == from) {
            const dev = state.devices[key];
            dev.deviceId = to + '';
            state.devices[to] = dev;
            delete state.devices[key];
        }
    }
    for (const key in state.frames) {
        const frame = state.frames[key];
        for (let i = 0; i < frame.deviceIds.length; i++) {
            if (frame.deviceIds[i] == from) {
                frame.deviceIds[i] = to + '';
                return;
            }
        }
    }
}

function changeSerialIdInState(state: AdjustFrameState, from: any, to: any) {
    const fromId = 'frame-' + from;
    for (const key in state.frames) {
        const frame = state.frames[key];
        if (fromId === key) {
            const newId = 'frame-' + to;
            frame.id = newId;
            state.frames[newId] = frame;
            delete state.frames[key];
            return;
        }
    }
}

function copyPropsFromFrameToSerial(frame: FrameState): Promise<unknown> {
    const actualSerial = getSerialById(frame.id.replace('frame-', ''));
    if (!actualSerial) return Promise.resolve(false);

    const origiCopy = { ...actualSerial };

    let devIdDifference = actualSerial.deviceIds.length !== frame.deviceIds.length;
    if (!devIdDifference) {
        for (let i = 0; i < actualSerial.deviceIds.length; i++) {
            if (actualSerial.deviceIds[i] != frame.deviceIds[i]) {
                devIdDifference = true;
                break;
            }
        }
    }

    const anyDifference =
        devIdDifference ||
        actualSerial.direction !== frame.direction ||
        actualSerial.height !== frame.height;

    if (!anyDifference) return Promise.resolve();

    actualSerial.deviceIds = frame.deviceIds.map((x) => +x);
    actualSerial.direction = frame.direction;
    actualSerial.height = +frame.height;

    return ajaxSetSerialProps(actualSerial, true).catch(() => {
        actualSerial.deviceIds = origiCopy.deviceIds;
        actualSerial.direction = origiCopy.direction;
        actualSerial.height = origiCopy.height;
        redrawDevices();
    });
}

function frameModChangeFrames(framesArr: FrameState[]): Promise<unknown[]> {
    return Promise.all(framesArr.map((frame) => copyPropsFromFrameToSerial(frame)));
}

function frameModMergeTwo(
    removedFrame: FrameState,
    keptFrame: FrameState,
    origiState: AdjustFrameState,
): Promise<void> {
    return new Promise((resolve, reject) => {
        const removedFrameSerial = getSerialById(removedFrame.id.replace('frame-', ''));
        const keptFrameSerial = getSerialById(keptFrame.id.replace('frame-', ''));
        if (!removedFrameSerial || !keptFrameSerial) return reject();

        const devsToMove = removedFrameSerial.deviceIds.map((devId: any) => getDeviceById(devId));

        const actions = devsToMove.map((dev: any) =>
            makeDeleteDeviceAction(dev, keptFrameSerial, false),
        );

        removeDevices(devsToMove, (removeRes: boolean) => {
            if (!removeRes) return reject();

            runUndoChain(
                actions,
                (a) => a,
                (_action, { oldDevId, newDevId }) => {
                    for (let i = 0; i < keptFrame.deviceIds.length; i++) {
                        if (keptFrame.deviceIds[i] == oldDevId) {
                            keptFrame.deviceIds[i] = newDevId + '';
                            break;
                        }
                    }
                    changeDevIdInState(origiState, oldDevId, newDevId);
                    addNewDesignIdToDefaultSerial(devsToMove[0].product_id, newDevId);
                },
            )
                .then(() => copyPropsFromFrameToSerial(keptFrame))
                .then(() => resolve())
                .catch(() => reject());
        });
    });
}

function frameModSplitSingle(
    newFrame: FrameState,
    oldFrame: FrameState,
    origiState: AdjustFrameState,
): Promise<void> {
    return new Promise((resolve, reject) => {
        const devsToMove = newFrame.deviceIds.map((devId) => getDeviceById(devId));

        const fakeSerial = {
            id: 'fake_' + +new Date(),
            deviceIds: Array.from(newFrame.deviceIds),
            height: newFrame.height,
            direction: newFrame.direction,
        };

        const actions = devsToMove.map((dev: any, idx: number) =>
            makeDeleteDeviceAction(dev, fakeSerial, idx === 0),
        );

        removeDevices(devsToMove, (removeRes: boolean) => {
            if (!removeRes) return reject();

            runUndoChain(
                actions,
                (a) => a,
                (_item, { action, oldDevId, newDevId }) => {
                    for (let i = 0; i < newFrame.deviceIds.length; i++) {
                        if (newFrame.deviceIds[i] == oldDevId) {
                            newFrame.deviceIds[i] = newDevId + '';
                            break;
                        }
                    }
                    changeDevIdInState(origiState, oldDevId, newDevId);
                    addNewDesignIdToDefaultSerial(devsToMove[0].product_id, newDevId);
                    if (action.wasLastOfSerial) {
                        actions.forEach((x: any) => (x.serialId = action.serialId));
                        changeSerialIdInState(origiState, 'new', action.serialId);
                        newFrame.id = 'frame-' + action.serialId;
                    }
                },
            )
                .then(() => {
                    newFrame.id = 'frame-' + actions[0].serialId;
                    const serials = [
                        getSerialById(actions[0].serialId),
                        getSerialById(oldFrame.id.replace('frame-', '')),
                    ];
                    return Promise.all([
                        copyPropsFromFrameToSerial(newFrame),
                        copyPropsFromFrameToSerial(oldFrame),
                    ])
                        .then(() => connectSerials(serials[0].id, serials[1].id))
                        .then(() => resolve())
                        .catch(() => reject());
                })
                .catch(() => reject());
        });
    });
}

function frameModConnectFramesIfNeeded(framesArr: FrameState[]): Promise<unknown> {
    if (!Array.isArray(framesArr) || framesArr.length !== 2) return Promise.resolve();
    const ser1 = getSerialById(framesArr[0].id.replace('frame-', ''));
    const ser2 = getSerialById(framesArr[1].id.replace('frame-', ''));
    if (ser1 && ser2 && !getSerialConnectionForSerialId(ser1.id) && !getSerialConnectionForSerialId(ser2.id)) {
        return connectSerials(ser1.id, ser2.id);
    }
    return Promise.resolve();
}

function frameModCorrectMixNMatchIfNeeded(
    framesArr: FrameState[],
    origiState: AdjustFrameState,
): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        const mixedDevObjs: { deviceId: string; targetSerialId: any; frame: FrameState }[] = [];

        let allDevsLen = 0;
        framesArr.forEach((frame) => {
            const actualSerial = getSerialById(frame.id.replace('frame-', ''));
            if (!actualSerial) return;
            allDevsLen += frame.deviceIds.length;
            frame.deviceIds.forEach((frameDevId) => {
                if (!actualSerial.deviceIds.find((x: any) => x == frameDevId)) {
                    mixedDevObjs.push({
                        deviceId: frameDevId,
                        targetSerialId: actualSerial.id,
                        frame,
                    });
                }
            });
        });

        if (!mixedDevObjs.length) return resolve();

        if (mixedDevObjs.length === allDevsLen) {
            const tempDir = framesArr[0].direction;
            const tempHeight = framesArr[0].height;
            const tempDevIds = framesArr[0].deviceIds;
            framesArr[0].direction = framesArr[1].direction;
            framesArr[0].height = framesArr[1].height;
            framesArr[0].deviceIds = framesArr[1].deviceIds;
            framesArr[1].direction = tempDir;
            framesArr[1].height = tempHeight;
            framesArr[1].deviceIds = tempDevIds;
            return resolve();
        }

        const firstDev = getDeviceById(mixedDevObjs[0].deviceId);
        const colorId = firstDev?.product_id;

        const devPlaceBackActions = mixedDevObjs.map((devObj) => {
            const dev = getDeviceById(devObj.deviceId);
            const serial = getSerialById(devObj.targetSerialId);
            const action = makeDeleteDeviceAction(dev, serial, false);
            return { action, frame: devObj.frame };
        });

        removeDevices(
            mixedDevObjs.map((devObj) => getDeviceById(devObj.deviceId)),
            () => {
                framesArr.forEach((frame) => {
                    const serId = frame.id.replace('frame-', '');
                    const actualSerial = getSerialById(serId);
                    if (!actualSerial || !actualSerial.deviceIds.length) {
                        const firstPlaceBack = devPlaceBackActions.find(
                            (aaf: any) => aaf.action.serialId == serId,
                        );
                        if (firstPlaceBack) {
                            firstPlaceBack.action.wasLastOfSerial = true;
                        }
                    }
                });

                runUndoChain(
                    devPlaceBackActions,
                    (i) => i.action,
                    ({ frame }, { action, oldDevId, oldSerId, newDevId }) => {
                        for (let i = 0; i < frame.deviceIds.length; i++) {
                            if (frame.deviceIds[i] == oldDevId) {
                                frame.deviceIds[i] = newDevId + '';
                                break;
                            }
                        }
                        addNewDesignIdToDefaultSerial(colorId, newDevId);
                        changeDevIdInState(origiState, oldDevId, newDevId);
                        if (action.wasLastOfSerial) {
                            devPlaceBackActions.forEach((elem: any) => {
                                if (elem.action.serialId == oldSerId) {
                                    elem.action.serialId = action.serialId;
                                }
                            });
                            changeSerialIdInState(origiState, oldSerId, action.serialId);
                            frame.id = 'frame-' + action.serialId;
                        }
                    },
                )
                    .then(() => resolve())
                    .catch(() => reject());
            },
        );
    }).then(() => {
        return frameModConnectFramesIfNeeded(Object.values(origiState.frames)) as Promise<void>;
    });
}

function snapBackIfNeeded(
    serialId: any,
    origiTopMidPt: any,
    origiPosProps: any,
    connPairId: any,
): Promise<unknown> {
    let activeSerialId = serialId;
    const actualSerial = getSerialById(activeSerialId);
    if (!actualSerial) {
        activeSerialId = connPairId;
    }

    const newConn = getSerialConnectionForSerialId(activeSerialId);
    const newDElem: any = newConn ? getSerialConnectionProps(newConn.id) : getSerialD(activeSerialId);
    if (!newDElem) return Promise.resolve();

    if (isPointSnapped(origiTopMidPt)) {
        const snappedPlace = snapDeviceToWall(
            origiTopMidPt,
            PlannerGlobals.DEVICE_SIZE * SNAP_SEARCH_RADIUS_DEVICE_WIDTHS,
            getDColsNum(newDElem),
            false,
            undefined,
            undefined,
            undefined,
            undefined,
            getDRowsNum(newDElem),
        );
        if (snappedPlace) {
            if (uMath.pointDistance(snappedPlace.point, origiPosProps) > 0.1) {
                const snapProps: any = snappedPtToPosData(snappedPlace);
                if (!snapProps) return Promise.resolve();
                if (newConn) {
                    return Promise.all(
                        newDElem.serials.map((ser: any) => setAndSendSerialPosition(ser.id, snapProps)),
                    );
                }
                return setAndSendSerialPosition(activeSerialId, snapProps);
            }
        } else {
            return Promise.resolve(false);
        }
        return Promise.resolve();
    }

    const isGood = isDeviceInIntendedRoom(newDElem);
    if (isGood) return Promise.resolve();

    const devRoom = getRoomById(newDElem.devices[0].roomId);
    rebuildWallsCache(devRoom, false, false);
    const snappedPlace = snapDeviceToWall(
        newDElem,
        PlannerGlobals.DEVICE_SIZE * SNAP_SEARCH_RADIUS_DEVICE_WIDTHS,
        getDColsNum(newDElem),
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        getDRowsNum(newDElem),
    );
    if (snappedPlace) {
        const snapProps: any = snappedPtToPosData(snappedPlace);
        if (!snapProps) return Promise.resolve();
        if (newConn) {
            return Promise.all(
                newDElem.serials.map((ser: any) => setAndSendSerialPosition(ser.id, snapProps)),
            );
        }
        return setAndSendSerialPosition(activeSerialId, snapProps);
    }
    return Promise.resolve(false);
}

export function confirmSave(
    origiStateInput: AdjustFrameState,
    stateInput: AdjustFrameState,
    addAction = false,
): Promise<void> {
    const origiState: AdjustFrameState = structuredClone(origiStateInput);
    const state: AdjustFrameState = structuredClone(stateInput);

    return new Promise<void>((resolve, reject) => {
        if (!state || !state.frames) return reject();

        const framesArr = Object.values(state.frames);
        const aliveFrame = framesArr.find((elem) => !!getSerialById(elem.id.replace('frame-', '')));
        if (!aliveFrame) return reject();
        const serialId = aliveFrame.id.replace('frame-', '');

        const conn = getSerialConnectionForSerialId(serialId);
        const connPairId = conn ? getSerialConnectedPair(serialId)?.id : null;
        const origiDElem: any = conn ? getSerialConnectionProps(conn.id) : getSerialD(serialId);
        if (!origiDElem) return reject();
        const origiPosProps = {
            x: origiDElem.x,
            y: origiDElem.y,
            angle: origiDElem.angle,
            roomId: origiDElem.roomId,
        };
        const origiTopMidPt = getD3DevTopCenter(origiDElem);

        const newFrame = framesArr.find((x) => x.isNew);
        const oldFrame = newFrame ? framesArr.find((x) => !x.isNew) : null;
        const removedFrame = !newFrame ? framesArr.find((x) => !x.deviceIds.length) : null;
        const keptFrame = removedFrame ? framesArr.find((x) => x.deviceIds.length) : null;

        let promise: Promise<unknown>;
        if (newFrame && newFrame.deviceIds.length && oldFrame && oldFrame.deviceIds.length) {
            promise = frameModSplitSingle(newFrame, oldFrame, origiState);
        } else if (removedFrame && keptFrame) {
            promise = frameModMergeTwo(removedFrame, keptFrame, origiState);
        } else {
            promise = frameModCorrectMixNMatchIfNeeded(framesArr, origiState).then(() =>
                frameModChangeFrames(framesArr),
            );
        }

        promise
            .then(() => snapBackIfNeeded(serialId, origiTopMidPt, origiPosProps, connPairId))
            .then(() => {
                if (addAction) {
                    const refreshedDElem: any = conn
                        ? getSerialConnectionProps(conn.id)
                        : getSerialD(serialId);
                    if (refreshedDElem) {
                        const newPosProps = {
                            x: refreshedDElem.x,
                            y: refreshedDElem.y,
                            angle: refreshedDElem.angle,
                            roomId: refreshedDElem.roomId,
                        };
                        origiState.posProps = origiPosProps;
                        state.posProps = newPosProps;
                        const action = new ModifyFramesAction(origiState, state);
                        PlannerGlobals.plannerHistoryManager?.addHistoryEntry(action);
                    }
                }
                UDevGlobals.devIdSerialCache = {};
                redrawDevices();
                resolve();
            })
            .catch((err: any) => reject(err));
    });
}

export class ModifyFramesAction extends PlannerAction {
    actionName: string;
    prevState: AdjustFrameState;
    nextState: AdjustFrameState;

    constructor(prevState: AdjustFrameState, nextState: AdjustFrameState) {
        super();
        this.actionName = ts('p_do_action_modframes', 'Modifying frames');
        this.prevState = { ...prevState };
        this.nextState = { ...nextState };
        [this.prevState, this.nextState].forEach((state: any) => {
            delete state.devices;
            delete state.serial;
            delete state.visible;
            for (const key in state.frames) {
                if (state.frames[key].deviceIds.length === 0) {
                    delete state.frames[key];
                }
            }
        });
    }

    goToStateFrom(prevState: AdjustFrameState, nextState: AdjustFrameState, callback: (ok: boolean) => void) {
        confirmSave(prevState, nextState, false)
            .then(() => callIfFn(callback, true))
            .catch(() => callIfFn(callback, false));
    }

    serialIdChanged(from: any, to: any) {
        [this.prevState, this.nextState].forEach((state) => {
            Object.values(state.frames).forEach((frame) => {
                const serId = frame.id.replace('frame-', '');
                if ((this as any).equalAsString(from, serId)) {
                    frame.id = 'frame-' + to;
                }
            });
        });
    }

    roomIdChanged(from: any, to: any) {
        [this.prevState, this.nextState].forEach((state) => {
            if (state.posProps && (this as any).equalAsString(from, state.posProps.roomId)) {
                state.posProps.roomId = to;
            }
        });
    }

    deviceIdChanged(from: any, to: any) {
        [this.prevState, this.nextState].forEach((state) => {
            Object.values(state.frames).forEach((frame) => {
                frame.deviceIds.forEach((id, idx, arr) => {
                    if ((this as any).equalAsString(from, id)) {
                        arr[idx] = to;
                    }
                });
            });
        });
    }

    execute(callback: (ok: boolean) => void) {
        this.goToStateFrom(this.prevState, this.nextState, callback);
    }

    undo(callback: (ok: boolean) => void) {
        for (const key in this.prevState.frames) {
            this.prevState.frames[key] = { ...this.prevState.frames[key] };
            this.prevState.frames[key].isNew = !(key in this.nextState.frames);
        }
        this.goToStateFrom(this.nextState, this.prevState, callback);
    }
}

export function cancelAdjustFrame() {
    UDevGlobals.devIdSerialCache = {};
    redrawDevices();
}

export function detachFrames(state: AdjustFrameState): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        const frames = Object.values(state.frames);
        if (frames.length < 2) return reject();

        const conn = getSerialConnectionForSerialId(frames[0].id.replace('frame-', ''));
        if (!conn) return reject();

        const connProps: any = getSerialConnectionProps(conn.id);
        if (!connProps) return reject();
        const prePos = {
            x: connProps.x,
            y: connProps.y,
            roomId: connProps.roomId,
            angle: connProps.angle,
        };

        detachSerialConnection(conn.id)
            .then(() => {
                const action = new DisconnectSerialsAction(
                    +frames[0].id.replace('frame-', ''),
                    +frames[1].id.replace('frame-', ''),
                    prePos,
                );
                PlannerGlobals.plannerHistoryManager?.addHistoryEntry(action);
                UDevGlobals.devIdSerialCache = {};
                redrawDevices();
                resolve();
            })
            .catch(() => reject());
    });
}
